import React, { useReducer } from 'react';
import { CmsActionTypes, CmsActions, CmsState, Status } from './typesUtil';

/**
 * Generates state object that holds CMS agnostic data for a CMS content type "T"
 * @returns State object with 1) data property that accepts values of type "T" or null, and 2) status property that represents the stage in data fetching process for "T".
 * @example const navigationState = generateCmsState<string>
 */
export function generateCmsState<T>(): CmsState<T> {
  const initialState: CmsState<T> = {
    status: Status.IDLE,
  };
  return initialState;
}

/**
 * Reducer function for CMS agnostic data for a CMS content type "T"
 * @param state Abstracted CMS data state of type "T"
 * @param action Actions associated with the state of type "T"
 * @returns Abstracted contentful data state of type "T"
 */
export function cmsReducer<T>(state: CmsState<T>, action: CmsActions<T>): CmsState<T> {
  switch (action.type) {
    case CmsActionTypes.ERROR:
      return {
        ...state,
        status: Status.ERRORED,
        message: action.payload,
      };
    case CmsActionTypes.SUCCESS:
      return {
        ...state,
        status: Status.RESOLVED,
        data: { ...action.payload },
      };
    case CmsActionTypes.FETCH:
      return {
        ...state,
        status: Status.PENDING,
      };
    default: {
      throw new Error(`Unhandled action type in cmsAbstractionReducer`);
    }
  }
}

/**
 * Generates CMS reducer function for a CMS content type "T"
 * @returns Contentful reducer function for type "T"
 */
export function generateCMSReducer<T>(): (state: CmsState<T>, action: CmsActions<T>) => CmsState<T> {
  return cmsReducer;
}

/**
 * Generates state and dispatch functions for a CMS content type "T"
 * @returns state and dispatch functions for type "T"
 */
export function generateCmsStateAndDispatch<T>(): {state: CmsState<T>; dispatch: React.Dispatch<CmsActions<T>>} {
  const initialState = generateCmsState<T>();
  const reducer = generateCMSReducer<T>();
  const [state, dispatch] = useReducer(reducer, initialState);
  return { state, dispatch };
}
